import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DSBackLink } from '../../design-system/primitives';
import { authService } from '../../services/authService';
import { getJson } from './scorekeepingApi';
import PublicHeader from './PublicHeader';
import { formatScore } from './format';
import './scorekeeping.css';

/**
 * P1. The public machine page.
 *
 * Where the club website's submit-score button lands, which makes it the front
 * door for anyone standing at a machine. No account required.
 *
 * The order of the page is deliberate and does not adapt. The button sits above
 * the full board, so somebody who has just played reaches it without reading,
 * and somebody who has not played yet scrolls past it to the scores they would
 * be chasing. The page cannot know which of the two is holding it, so it serves
 * both in one fixed order rather than guessing.
 *
 * There used to be a three-row strip of the top scores above the button, on the
 * theory that a newcomer should see the target without scrolling. On a real
 * board it was the same three rows printed twice on one screen, which reads as a
 * mistake rather than as emphasis.
 *
 * The board is not truncated. Five rows is the right length for a page about the
 * best players and the wrong one for a club trying to get beginners to come back:
 * somebody in 23rd who cannot see themselves has no evidence they are on the
 * board at all, and "Show all 60" asks them to believe it first and check second.
 */

const STATUS_LABEL = {
  operational: 'Playing well',
  needs_attention: 'Needs attention',
  out_of_order: 'Down',
  maintenance: 'In maintenance',
  in_tournament: 'In a tournament',
  removed: 'No longer here'
};

const STATUS_TONE = {
  operational: 'ok',
  needs_attention: 'warn',
  out_of_order: 'down',
  maintenance: 'warn',
  in_tournament: 'info',
  removed: 'down'
};

function MachineScorePage() {
  const { machineId } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [error, setError] = useState(null);
  const [howOpen, setHowOpen] = useState(false);
  // authService.getCurrentUser() parses localStorage and hands back a fresh object
  // every call, so it has a new identity on every render. Read once and hold it:
  // anything that takes it as a dependency otherwise rebuilds on every render, and
  // a fetch keyed on that turns into an unbounded request loop.
  const currentUser = useMemo(() => authService.getCurrentUser(), []);

  const load = useCallback(async () => {
    try {
      setPage(await getJson(`/machines/${machineId}/page`));
    } catch (err) {
      setError(err.message);
    }
  }, [machineId]);

  useEffect(() => { load(); }, [load]);

  if (error) {
    return (
      <div className="sk-page">
        <div className="sk-shell">
          <h1 className="sk-h1">Machine not found</h1>
          <p className="sk-muted">That code may be out of date. Ask at the desk.</p>
        </div>
      </div>
    );
  }

  if (!page) return <div className="sk-page"><div className="sk-shell sk-muted">Loading…</div></div>;

  const { machine, entries, label } = page;

  return (
    <div className="sk-page">
      {/* Only for guests. A signed-in member already has the application's own
          header above this, and two brand bars stacked is worse than none. */}
      {!currentUser && <PublicHeader signInRedirect={`/scorekeeping/machines/${machineId}`} />}

      <div className="sk-shell">
        {/* The artwork runs to the edges with the way back over it, the same
            shape the PinGolf hero uses. Two things come out of that: the page
            gets a back control it did not have at all, and the machine gets a
            picture big enough to recognise from across the room.

            The hero renders whether or not there is artwork, because the back
            link is not decoration. A machine with no image gets a plain surface
            and everything else still works. */}
        <header className="sk-hero">
          {machine.imageUrl
            ? <img className="sk-hero__art" src={machine.imageUrl} alt="" />
            : <div className="sk-hero__art sk-hero__art--empty" />}

          <div className="sk-hero__content">
            {/* A fixed destination rather than browser history. This page is
                reached by scanning the code on the machine as often as by tapping
                through, and history is a dead end for the person standing in
                front of it. */}
            <DSBackLink to="/scorekeeping/leaderboard">Leaderboard</DSBackLink>

            <div className="sk-hero__titles">
              <p className="sk-hero__meta">
                {[machine.manufacturer, machine.year].filter(Boolean).join(' · ')}
              </p>
              <h1 className="sk-hero__title">{machine.name}</h1>
            </div>
          </div>
        </header>

        {machine.status === 'out_of_order' && (
          <div className="sk-banner sk-banner--warn">
            <strong>Out of order</strong>
            <span>Played it before it went down? Your score still counts.</span>
          </div>
        )}

        <div className="sk-machine">
          {/* Two chips, so they stay on one line. The Insider Connected chip was
              the third and the one that pushed them onto a second row, and it was
              also the least useful: it labelled the machine's hardware, which the
              "How do I record my score?" line below explains properly to the only
              people who need to know. */}
          <div className="sk-chips">
            <span className={`sk-chip sk-chip--${STATUS_TONE[machine.status] || 'info'}`}>
              {STATUS_LABEL[machine.status] || machine.status}
            </span>
            <span className={`sk-chip ${machine.countsTowardCompetition ? 'sk-chip--ok' : 'sk-chip--muted'}`}>
              {machine.countsTowardCompetition
                ? `Counts in ${label.split(' ')[0]}`
                : `Not counting in ${label.split(' ')[0]}`}
            </span>
          </div>
          {!machine.countsTowardCompetition && (
            <p className="sk-muted sk-machine__note">No competition points this month.</p>
          )}
        </div>

        {/* A Stern machine explains itself rather than going quiet. A page with no
            way to enter a score and no explanation reads as broken. */}
        {/* A Stern machine has no button to press, so the page has to account for
            itself or it reads as broken. It does that in one line rather than a
            card: the answer matters on a first visit and never again, and it was
            taking the space the board should have, above the board, every time. */}
        {!machine.acceptsManualEntry ? (
          <div className="sk-howto">
            <button
              type="button"
              className="sk-howto__toggle"
              aria-expanded={howOpen}
              onClick={() => setHowOpen(!howOpen)}
            >
              How do I record my score?
              <span className="sk-howto__chev" aria-hidden="true">{howOpen ? '−' : '+'}</span>
            </button>

            {howOpen && (
              <div className="sk-howto__body">
                <h2 className="sk-h2">This machine records your score for you</h2>
                <p className="sk-muted">
                  Sign in to Stern Insider Connected at the machine and your score goes to the board
                  on its own. There is nothing to type and no photo to take.
                </p>
                <ol className="sk-steps">
                  <li>Open Insider Connected on your phone and show your code to the camera on the machine.</li>
                  <li>Any time before your last ball drains is fine. Mid-game counts.</li>
                  <li>Your score shows up here within a minute of the game ending.</li>
                </ol>
                <p className="sk-automatic__caveat">
                  Didn&apos;t sign in? That game can&apos;t be added afterwards. Sign in first and play again.
                </p>
              </div>
            )}
          </div>
        ) : (
          <button
            type="button"
            className="sk-cta"
            onClick={() => navigate(`/scorekeeping/machines/${machineId}/submit`)}
          >
            Submit a score
          </button>
        )}

        <section className="sk-card">
          <p className="sk-eyebrow">
            {label} board{entries.length > 0 ? ` · ${entries.length} player${entries.length === 1 ? '' : 's'}` : ''}
          </p>

          {entries.length === 0 ? (
            <div className="sk-empty">
              <p className="sk-empty__title">Nobody has posted a score yet</p>
              <p className="sk-muted">
                Whatever you put up is first place until somebody beats it.
              </p>
            </div>
          ) : (
            <table className="sk-board">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Player</th>
                    <th className="sk-num">Score</th>
                    <th className="sk-num">Pts</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry) => (
                    <tr
                      key={`${entry.rank}-${entry.playerName}`}
                      className={currentUser && entry.userId === currentUser.id ? 'is-me' : ''}
                    >
                      <td>{entry.rank}</td>
                      <td className="sk-board__player">{entry.playerName}</td>
                      <td className="sk-num">{formatScore(entry.score)}</td>
                      <td className="sk-num sk-board__points">{entry.points || '—'}</td>
                    </tr>
                  ))}
                </tbody>
            </table>
          )}
        </section>

      </div>
    </div>
  );
}

export default MachineScorePage;
